/**
 * The step-by-step "Add a song" flow of the Catalog (design `docs/superpowers/design-reference/catalog-add-song/`,
 * owner answers 2026-10-09). The same rules serve music24-api and the web app: the steps, the
 * "draft" rule of the Releases list, the next step that "Finish setup" opens, and the UPC check.
 *
 * The file has no imports, so every project can use it.
 */

/** The steps in their order. `codes` asks for the ISRC and the UPC of an upcoming song, or the link of a released one. */
export const ADD_SONG_STEPS = ['song', 'release', 'codes', 'cover', 'audio', 'lyrics', 'done'] as const;

export type AddSongStep = (typeof ADD_SONG_STEPS)[number];

/** True for a text that names a step. */
export function isAddSongStep(value: string | null | undefined): value is AddSongStep {
  return value !== null && value !== undefined && (ADD_SONG_STEPS as readonly string[]).includes(value);
}

/** The facts of a catalog row that the flow reads. */
export interface AddSongFacts {
  /** `unreleased` for a typed song, `claimed` for a song that is out (claimed, linked or pasted). */
  kind: 'claimed' | 'unreleased';
  /** `YYYY-MM-DD` (or an ISO time), or null. */
  releaseDate: string | null;
  hasCover: boolean;
  hasMaster: boolean;
  hasLyrics: boolean;
}

/**
 * A draft is a typed song that has only its title so far: not out, no release date and no cover
 * (owner answer 1, 2026-10-09). The Releases list shows it as "Draft" with "Finish setup".
 */
export function isDraftSong(facts: AddSongFacts): boolean {
  return facts.kind === 'unreleased' && facts.releaseDate === null && !facts.hasCover;
}

/**
 * The first step that still misses something, for "Finish setup": the release date of a typed
 * song, the cover, the audio, the lyrics. `done` when nothing is missing. The ISRC and the UPC are
 * never asked again: they are optional and the song page edits them.
 */
export function nextAddSongStep(facts: AddSongFacts): AddSongStep {
  if (facts.kind === 'unreleased' && facts.releaseDate === null) {
    return 'release';
  }
  if (!facts.hasCover) {
    return 'cover';
  }
  if (!facts.hasMaster) {
    return 'audio';
  }
  if (!facts.hasLyrics) {
    return 'lyrics';
  }
  return 'done';
}

const UPC_PATTERN = /^[0-9]{12,14}$/;

/**
 * The UPC (or EAN / GTIN) of a release as music24-api stores it: digits only, 12 to 14 of them.
 * Spaces and dashes are ignored. Null when the text is not such a code.
 */
export function normalizeUpc(input: string): string | null {
  const value = input.replace(/[-\s]/g, '');
  return UPC_PATTERN.test(value) ? value : null;
}

/** The sentence of a refused UPC (the server 400 and the form line). */
export const INVALID_UPC_MESSAGE = 'A UPC has 12 to 14 numbers.';
